import React, { useMemo } from 'react';
import { Modal } from 'antd';
import { Formik } from 'formik';
import { Col, Row } from 'react-bootstrap';
import * as Yup from 'yup';
import Input from '../input';
import { formatUtcDateTimeForInput } from '../../utils/helper';
import { FiCheckCircle, FiClock, FiPackage, FiTruck, FiCheck, FiLayers, FiAlertCircle } from 'react-icons/fi';
import { MdOutlineLocalShipping } from 'react-icons/md';
import '../../styles/TripCompleteModal.css';

const parseNumber = (value) => {
    if (value === '' || value === null || value === undefined) return null;
    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? null : numericValue;
};

const formatDateTimeForInput = (value) => {
    if (!value) return '';

    const textValue = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(textValue)) {
        return textValue;
    }

    return formatUtcDateTimeForInput(textValue);
};

const buildValidationSchema = ({ startOdometer, startDateTime, totalQuantity = 0 }) => {
    const parsedStartOdometer = parseNumber(startOdometer);

    return Yup.object().shape({
        end_odm: Yup.number()
            .transform((value, originalValue) => (originalValue === '' ? null : value))
            .nullable()
            .typeError('End Odometer must be a number')
            .required('End Odometer is required')
            .min(0, 'Must be positive')
            .test(
                'greater-than-start-odometer',
                'End Odometer Reading must be greater than Start Odometer',
                (value) => value === null || value === undefined || parsedStartOdometer === null || value > parsedStartOdometer
            ),
        closing_fuel: Yup.number()
            .transform((value, originalValue) => (originalValue === '' ? null : value))
            .nullable()
            .typeError('Closing Fuel must be a number')
            .required('Closing Fuel is required')
            .min(0, 'Must be positive'),
        delivered_qty: Yup.number()
            .transform((value, originalValue) => (originalValue === '' ? null : value))
            .nullable()
            .typeError('Delivered Quantity must be a number')
            .required('Delivered Quantity is required')
            .min(0, 'Delivered Quantity cannot be less than 0'),
        shortage_qty: Yup.number()
            .transform((value, originalValue) => (originalValue === '' ? null : value))
            .nullable()
            .when(['delivered_qty'], ([delQty], schema) => {
                const numDel = Number(delQty);
                if (totalQuantity > 0 && !Number.isNaN(numDel) && numDel >= totalQuantity) {
                    return schema.notRequired().nullable();
                }
                return schema
                    .typeError('Shortage Qty must be a number')
                    .required('Shortage Qty is required')
                    .min(0, 'Shortage Qty cannot be less than 0');
            }),
        relief_qty: Yup.number()
            .transform((value, originalValue) => (originalValue === '' ? null : value))
            .nullable()
            .when(['delivered_qty'], ([delQty], schema) => {
                const numDel = Number(delQty);
                if (totalQuantity > 0 && !Number.isNaN(numDel) && numDel >= totalQuantity) {
                    return schema.notRequired().nullable();
                }
                return schema
                    .typeError('Relief Qty must be a number')
                    .required('Relief Qty is required')
                    .min(0, 'Relief Qty cannot be less than 0');
            }),
        actual_end_at: Yup.string()
            .required('Actual End Date & Time is required')
            .test(
                'not-before-start-date-time',
                'Actual End Date & Time cannot be earlier than Start Date & Time',
                (value) => {
                    if (!value || !startDateTime) return true;

                    const start = new Date(startDateTime);
                    const end = new Date(value);

                    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
                        return true;
                    }

                    return end.getTime() >= start.getTime();
                }
            ),
    });
};

const TripCompleteModal = ({
    open,
    onClose,
    onConfirm,
    loading = false,
    initialValues,
    centered = true,
}) => {
    const parsedStartOdometer = parseNumber(initialValues?.startOdometer);

    // Resolve Total Order / Planned Quantity from available trip data
    const totalQuantity = useMemo(() => {
        const rawVal =
            initialValues?.totalQuantity ??
            initialValues?.total_load ??
            initialValues?.total_quantity ??
            initialValues?.trip_load_sum?.total_load ??
            initialValues?.totalLoad ??
            initialValues?.quantity;

        if (rawVal !== undefined && rawVal !== null && rawVal !== '' && !Number.isNaN(Number(rawVal)) && Number(rawVal) > 0) {
            return Number(rawVal);
        }

        const stops = initialValues?.trip_stops || initialValues?.stops || initialValues?.raw?.trip_stops || [];
        if (Array.isArray(stops) && stops.length > 0) {
            const loadStops = stops.filter((s) => s.stop_type === 'load');
            const loadQty = loadStops.reduce((sum, s) => sum + (Number(s.quantity) || 0), 0);
            if (loadQty > 0) return loadQty;
            const allQty = stops.reduce((sum, s) => sum + (Number(s.quantity) || 0), 0);
            if (allQty > 0) return allQty;
        }

        return 0;
    }, [initialValues]);

    return (
        <Modal
            footer={null}
            mask={{ enabled: true, blur: true }}
            centered={centered}
            open={open}
            onCancel={onClose}
            width={640}
            className="fms_complete_trip_modal"
            destroyOnClose
        >
            <div className="complete_trip_modal_wrapper">
                {/* Header */}
                <div className="complete_trip_header">
                    <div className="complete_trip_header_icon">
                        <MdOutlineLocalShipping size={24} />
                    </div>
                    <div className="complete_trip_header_text">
                        <h3 className="complete_trip_header_title">Complete Trip</h3>
                        <p className="complete_trip_header_sub">
                            Provide closing odometer, fuel, and cargo delivery readings to complete this trip.
                        </p>
                    </div>
                </div>

                {/* Optional Trip Context Card */}
                {(initialValues?.vehicleReg || initialValues?.customerName || parsedStartOdometer !== null) && (
                    <div className="complete_trip_context_card">
                        {initialValues?.vehicleReg && (
                            <div className="context_chip">
                                <FiTruck size={14} color="#059669" />
                                <span>Vehicle: <strong>{String(initialValues.vehicleReg).toUpperCase()}</strong></span>
                            </div>
                        )}
                        {initialValues?.customerName && (
                            <>
                                <div className="context_chip_divider" />
                                <div className="context_chip">
                                    <span>Customer: <strong>{initialValues.customerName}</strong></span>
                                </div>
                            </>
                        )}
                        {parsedStartOdometer !== null && (
                            <>
                                <div className="context_chip_divider" />
                                <div className="context_chip">
                                    <span>Start Odo: <strong>{parsedStartOdometer.toLocaleString()} km</strong></span>
                                </div>
                            </>
                        )}
                    </div>
                )}

                <Formik
                    initialValues={{
                        end_odm: initialValues?.endOdometer || '',
                        closing_fuel: initialValues?.closing_fuel || '',
                        delivered_qty: '',
                        shortage_qty: '',
                        relief_qty: '',
                        actual_end_at: formatDateTimeForInput(
                            initialValues?.actualEndDateTime || initialValues?.actual_end_at
                        ),
                    }}
                    validationSchema={buildValidationSchema({
                        startOdometer: initialValues?.startOdometer,
                        startDateTime: initialValues?.startDateTime,
                        totalQuantity,
                    })}
                    enableReinitialize
                    onSubmit={(values) => {
                        const numDel = values.delivered_qty === '' ? 0 : Number(values.delivered_qty);
                        const isFullyDeliveredOnSubmit = totalQuantity > 0 && numDel >= totalQuantity;

                        onConfirm({
                            ...values,
                            delivered_qty: numDel,
                            shortage_qty: isFullyDeliveredOnSubmit ? 0 : (values.shortage_qty === '' ? 0 : Number(values.shortage_qty)),
                            relief_qty: isFullyDeliveredOnSubmit ? 0 : (values.relief_qty === '' ? 0 : Number(values.relief_qty)),
                        });
                    }}
                >
                    {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, submitCount }) => {
                        const parsedEndOdometer = parseNumber(values.end_odm);
                        const isEndOdometerInvalid =
                            parsedStartOdometer !== null &&
                            parsedEndOdometer !== null &&
                            parsedEndOdometer <= parsedStartOdometer;
                        const endOdometerError = isEndOdometerInvalid
                            ? 'End Odometer Reading must be greater than Start Odometer'
                            : errors.end_odm;

                        const showFieldError = (field) => Boolean(touched[field] || submitCount > 0);

                        const distanceCalculated =
                            parsedStartOdometer !== null && parsedEndOdometer !== null && parsedEndOdometer > parsedStartOdometer
                                ? (parsedEndOdometer - parsedStartOdometer).toFixed(1)
                                : null;

                        const numDelivered = values.delivered_qty === '' ? null : Number(values.delivered_qty);

                        // If delivered qty is >= total delivery, shortage and relief become 0.00 and DISABLED
                        const isFullyDelivered = Boolean(
                            totalQuantity > 0 &&
                            numDelivered !== null &&
                            !Number.isNaN(numDelivered) &&
                            numDelivered >= totalQuantity
                        );

                        // Dynamic calculation handlers
                        const handleDeliveredChange = (e) => {
                            const val = e.target.value;
                            setFieldValue('delivered_qty', val);

                            if (totalQuantity > 0) {
                                if (val === '' || val === null || val === undefined) {
                                    setFieldValue('shortage_qty', '');
                                    setFieldValue('relief_qty', '');
                                    return;
                                }

                                const numDel = Number(val);
                                if (!Number.isNaN(numDel) && numDel >= totalQuantity) {
                                    setFieldValue('shortage_qty', '0.00');
                                    setFieldValue('relief_qty', '0.00');
                                } else {
                                    const computedShortage = Math.max(0, totalQuantity - numDel);
                                    setFieldValue('shortage_qty', computedShortage % 1 === 0 ? String(computedShortage) : computedShortage.toFixed(2));
                                }
                            }
                        };

                        const handleReliefChange = (e) => {
                            if (isFullyDelivered) return;
                            setFieldValue('relief_qty', e.target.value);
                            // Note: Relief does not affect shortage qty, shortage remains Total - Delivered
                        };

                        const handleShortageChange = (e) => {
                            if (isFullyDelivered) return;
                            setFieldValue('shortage_qty', e.target.value);
                        };

                        const numShortage = isFullyDelivered ? 0 : (values.shortage_qty === '' ? 0 : Number(values.shortage_qty));
                        const numRelief = isFullyDelivered ? 0 : (values.relief_qty === '' ? 0 : Number(values.relief_qty));

                        return (
                            <form onSubmit={handleSubmit} className="complete_trip_form">
                                {/* Section 1: Final Vehicle Readings */}
                                <div className="complete_trip_section">
                                    <div className="complete_trip_section_title">
                                        <FiClock size={13} />
                                        <span>Final Vehicle Readings</span>
                                    </div>
                                    <Row className="g-3">
                                        <Col xs={12} md={6}>
                                            <Input
                                                label="Actual End Date & Time"
                                                type="datetime-local"
                                                name="actual_end_at"
                                                value={values.actual_end_at}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={errors.actual_end_at}
                                                touched={showFieldError('actual_end_at')}
                                                required
                                            />
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <Input
                                                label="End Odometer Reading"
                                                type="number"
                                                placeholder="0.00"
                                                name="end_odm"
                                                value={values.end_odm}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={endOdometerError}
                                                touched={showFieldError('end_odm')}
                                                step="any"
                                                required
                                            />
                                        </Col>
                                    </Row>
                                    {distanceCalculated !== null && (
                                        <div className="complete_trip_helper_row">
                                            <span className="complete_trip_pill distance">
                                                <FiCheck size={12} /> Distance Covered: <strong>{Number(distanceCalculated).toLocaleString()} km</strong>
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Section 2: Fuel & Cargo Quantities */}
                                <div className="complete_trip_section">
                                    <div className="complete_trip_section_title accent">
                                        <FiPackage size={13} />
                                        <span>Cargo & Fuel Details</span>
                                    </div>

                                    {/* Prominent Quantity Display Card */}
                                    <div className="complete_trip_quantity_card">
                                        <div className="qty_summary_main">
                                            <div className="qty_summary_icon">
                                                <FiLayers size={18} />
                                            </div>
                                            <div className="qty_summary_info">
                                                <span className="qty_summary_label">Total Order Quantity</span>
                                                <span className="qty_summary_value">
                                                    {totalQuantity > 0 ? `${totalQuantity.toLocaleString()} Units` : 'Not specified'}
                                                </span>
                                            </div>
                                        </div>
                                        {totalQuantity > 0 && numDelivered !== null && (
                                            <div className="qty_live_status_tag">
                                                {isFullyDelivered ? (
                                                    <span className="tag_delivered_full">
                                                        <FiCheck size={12} /> 100% Delivered
                                                    </span>
                                                ) : (
                                                    <span className="tag_shortage_active">
                                                        <FiAlertCircle size={12} /> Shortage: {numShortage}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <Row className="g-3">
                                        <Col xs={12} md={6}>
                                            <div className="delivered_qty_field_wrapper">
                                                <Input
                                                    label="Delivered Quantity"
                                                    type="number"
                                                    placeholder="0.00"
                                                    name="delivered_qty"
                                                    value={values.delivered_qty}
                                                    onChange={handleDeliveredChange}
                                                    onBlur={handleBlur}
                                                    error={errors.delivered_qty}
                                                    touched={showFieldError('delivered_qty')}
                                                    step="any"
                                                    required
                                                />
                                            </div>
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <Input
                                                label="Closing Fuel (ltrs.)"
                                                type="number"
                                                placeholder="0.00"
                                                name="closing_fuel"
                                                value={values.closing_fuel}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={errors.closing_fuel}
                                                touched={showFieldError('closing_fuel')}
                                                step="any"
                                                required
                                            />
                                        </Col>
                                    </Row>

                                    <Row className="g-3 mt-1">
                                        <Col xs={12} md={6}>
                                            <Input
                                                label="Relief Qty"
                                                type="number"
                                                placeholder="0.00"
                                                name="relief_qty"
                                                value={isFullyDelivered ? '0.00' : values.relief_qty}
                                                onChange={handleReliefChange}
                                                onBlur={handleBlur}
                                                error={isFullyDelivered ? null : errors.relief_qty}
                                                touched={showFieldError('relief_qty')}
                                                disabled={isFullyDelivered}
                                                required={!isFullyDelivered}
                                                step="any"
                                            />
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <Input
                                                label="Shortage Qty"
                                                type="number"
                                                placeholder="0.00"
                                                name="shortage_qty"
                                                value={isFullyDelivered ? '0.00' : values.shortage_qty}
                                                onChange={handleShortageChange}
                                                onBlur={handleBlur}
                                                error={isFullyDelivered ? null : errors.shortage_qty}
                                                touched={showFieldError('shortage_qty')}
                                                disabled={isFullyDelivered}
                                                required={!isFullyDelivered}
                                                step="any"
                                            />
                                        </Col>
                                    </Row>

                                    {numDelivered !== null && totalQuantity > 0 && (
                                        <div className="complete_trip_helper_row mt-2">
                                            {isFullyDelivered ? (
                                                <span className="complete_trip_pill zero_shortage">
                                                    <FiCheck size={12} /> Full quantity delivered ({numDelivered} / {totalQuantity}) — Shortage & Relief set to 0.00 (Disabled)
                                                </span>
                                            ) : (
                                                <span className={`complete_trip_pill ${numShortage > 0 ? 'net_shortage' : 'zero_shortage'}`}>
                                                    Shortage: {numShortage} (Total {totalQuantity} - Delivered {numDelivered})
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Footer Buttons */}
                                <div className="complete_trip_footer">
                                    <button
                                        type="button"
                                        className="btn_complete_cancel"
                                        onClick={onClose}
                                        disabled={loading}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn_complete_submit"
                                        disabled={loading}
                                    >
                                        <FiCheckCircle size={16} />
                                        <span>{loading ? 'Completing Trip...' : 'Complete Trip'}</span>
                                    </button>
                                </div>
                            </form>
                        );
                    }}
                </Formik>
            </div>
        </Modal>
    );
};

export default TripCompleteModal;
